<template>
  <v-menu
    offset-y
    min-width="300px"
    :max-height="props.menuHeight"
    :close-on-content-click="false"
    transition="slide-y-transition"
    rounded="xl"
  >
    <template #activator="{ on: menu }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltip }">
          <vs-button
            :label="buttonLabel"
            data-test="invoice-column-selector-button"
            icon="mdi-view-week"
            type="secondary"
            small
            v-on="{ ...tooltip, ...menu }"
          />
        </template>
        <span>Organize Columns</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col>
            <v-text-field v-model="searchValue" dense autofocus hide-details clearable>
              <template #append>
                <v-icon>search</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col class="shrink">
            <v-switch
              v-model="showHiddenColumns"
              class="mt-0"
              inset
              label="Show hidden"
              hide-details
              dense
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-actions>
        <span class="reset-button" role="button" @click="handleReset">Reset</span>
      </v-card-actions>
      <v-data-table
        hide-default-header
        item-key="selector"
        :items="orderedColumns"
        disable-pagination
        hide-default-footer
      >
        <template #body="{ items }">
          <component
            :is="searchValue ? 'tbody' : draggable"
            v-model="orderedColumnModel"
            tag="tbody"
            ghost-class="color-grey--lightest"
            animation="300"
            handle=".sortable"
          >
            <tr
              v-for="column in items"
              :key="column.selector"
              :class="{
                hidden:
                  !searchValue && showHiddenColumns ? false : !filteredColumns.includes(column),
              }"
              @click="handleToggle(column)"
            >
              <td style="width: 24px">
                <v-simple-checkbox
                  color="primary"
                  :value="store.visibleColumns.includes(column.selector)"
                  @click="handleToggle(column)"
                />
              </td>
              <td>{{ column.text }}</td>
              <td v-if="!searchValue" style="width: 24px">
                <v-icon class="sortable" style="cursor: pointer">drag_indicator</v-icon>
              </td>
            </tr>
          </component>
        </template>
        <template #no-results>Nothing found</template>
      </v-data-table>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import draggable from 'vuedraggable'
import { useBatchColumnStore } from './useBatchColumnStore'
import type { ColumnHeaders } from './useOrderedBatchColumn'

const props = withDefaults(
  defineProps<{
    columns: ColumnHeaders[]
    menuHeight?: string
  }>(),
  {
    menuHeight: '80vh',
  },
)

const store = useBatchColumnStore()
const searchValue = ref<string | null>(null)
const showHiddenColumns = ref(true)

const orderedColumns = computed(() => {
  const ordering = store.columnOrdering
  const allCols = props.columns

  const ordered: ColumnHeaders[] = []
  for (const sel of ordering) {
    const col = allCols.find((c) => c.selector === sel)
    if (col) ordered.push(col)
  }
  // Append any columns not yet in the ordering (newly added)
  for (const col of allCols) {
    if (!ordering.includes(col.selector)) {
      ordered.push(col)
    }
  }
  return ordered
})

const filteredColumns = computed(() =>
  orderedColumns.value.filter(
    (c) =>
      (showHiddenColumns.value || store.visibleColumns.includes(c.selector)) &&
      (!searchValue.value || c.text.toLowerCase().includes(searchValue.value.toLowerCase())),
  ),
)

const buttonLabel = computed(() => `${store.visibleColumns.length} of ${props.columns.length}`)

const orderedColumnModel = computed({
  get() {
    return orderedColumns.value
  },
  set(newOrder: ColumnHeaders[]) {
    store.setColumnOrdering(newOrder.map((c) => c.selector))
  },
})

function handleToggle(column: ColumnHeaders) {
  store.toggleColumn(column.selector)
}

function handleReset() {
  store.resetColumns(
    props.columns.map((c) => c.selector),
    props.columns.map((c) => c.selector),
  )
}
</script>

<style scoped>
.reset-button {
  font-size: 0.75rem;
  height: 1.5rem;
  margin: 4px 8px 4px 0;
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  line-height: 1.25;
  max-width: 100%;
  padding: 0 0.75em;
  position: relative;
  text-decoration: none;
  transition-duration: 0.28s;
  transition-property: box-shadow, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  vertical-align: middle;
  white-space: nowrap;
}

tr.hidden {
  display: none;
}

td {
  max-width: 100%;
  white-space: nowrap;
}
</style>
